import React, { useEffect, useState } from "react";
import { Container } from "reactstrap";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/CommonElements/PageTitle";
import { TableC } from "@/CommonElements/TableC";
import { useLocalizedText } from "@utils/hooks/useLocalizedText";
import { PrinterIcon, ViewIcon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { SelectCountry } from "@Component/Form/SelectCountry/SelectCountry";
import {
  useCreatePilgrimPassportReceiptMutation,
  useGetPilgrimsQuery,
  usePrintPilgrimPassportReceiptMutation,
} from "@services/piglgrims";
import { useDebouncedSearch } from "@utils/hooks/useDebouncedSearch";
import { LabelValue } from "@/Component/Value";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions";
import { DATE_TIME_FORMAT_INPUT, PILGRIM_STATUS, permissions } from "@/utils/constants";
import { DatePicker, Tag } from "antd";
import dayjs from "dayjs";
import { AntInput } from "@/CommonElements/AntInput";
import { AntSelect } from "@/CommonElements/AntSelect";
import PilgrimPrintSheet from "./PilgrimsDetails/PilgrimPrintSheet";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Label, Col, Row } from "reactstrap";
import { useCustomContext } from "@/_helper/customizer";
import { Stack } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { handleFormErrors } from "@/utils/helpers";
import { toast } from "react-toastify";
import { Can } from "@/CommonElements/PermissionGate";
import { useSystem } from "@/utils/hooks/useLocation";
import { SYSTEMS } from "@/utils/permissions";

const DOCUMENT_LOADING_CURSOR_OVERLAY_STYLE = {
  position: "fixed",
  inset: 0,
  cursor: "wait",
  zIndex: 9999,
  background: "transparent",
};

const Pilgrims = () => {
  const { t } = useTranslation();
  const { layout } = useCustomContext();
  const { getLocalizedText, getLocalizedNumber } = useLocalizedText();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({});
  const [printPilgrimId, setPrintPilgrimId] = useState(null);
  const [printRequestKey, setPrintRequestKey] = useState(null);
  const [isReceiveModalOpen, setIsReceiveModalOpen] = useState(false);
  const [selectedPilgrim, setSelectedPilgrim] = useState(null);
  const { currentSystem } = useSystem();
  const isMakkah = currentSystem === SYSTEMS.MAKKAH.code;
  // Use debounced search hook
  const { searchValue, debouncedValue, setSearchValue } = useDebouncedSearch("", 500);

  const { data: pilgrimsRes, isLoading } = useGetPilgrimsQuery({
    page,
    per_page: 10,
    search: debouncedValue,
    ...filters,
  });
  const { mutate: createPassportReceipt, isPending: isCreatePassportReceiptPending } =
    useCreatePilgrimPassportReceiptMutation();
  const { mutate: printPassportReceipt, isPending: isPrintPassportReceiptPending } =
    usePrintPilgrimPassportReceiptMutation();

  // Reset page to 1 when filters or search change
  useEffect(() => {
    setPage(1);
  }, [debouncedValue, filters]);

  const columns = [
    {
      name: t("PILGRIM_NUMBER"),
      key: "border_number",
      render: (value, rowData) => (
        <LabelValue
          value={value}
          copyValue={value}
          buttonIconSize={12}
          renderValue={() => (
            <Link className="fw-bold" to={`${rowData?.id}/view`}>
              {getLocalizedNumber(value ?? "-")}
            </Link>
          )}
        />
      ),
    },
    {
      name: t("PILGRIM_NAME"),
      key: "name",
    },
    {
      name: t("PASSPORT_NUMBER"),
      key: "passport_number",
      render: (value, rowData) => <LabelValue value={value} copyValue={value} buttonIconSize={12} />,
    },
    {
      name: t("NATIONALITY"),
      key: "nationality_id",
      render: (value) => <SelectCountry.Display value={value} />,
    },
    {
      name: t("GENDER"),
      key: "gender",
      render: (value) => getLocalizedText(value?.ar ?? "-", value?.en ?? "-"),
    },
    { name: t("PILGRIM_TYPE"), key: "haj_type" },
    {
      name: t("PILGRIM_STATUS"),
      key: "pilgrim_status",
      render: (value) => (
        <Tag color={value?.color ?? "red"}>{getLocalizedText(value?.label_ar ?? "-", value?.label_en ?? "-")}</Tag>
      ),
    },
    {
      name: t("CAMPAIGN_OR_GROUP_NUMBER"),
      key: "campaign_or_group_id",
    },
    {
      name: t("ENTRY_PORT"),
      key: "entry_port_id",
      render: (value) => {
        return (
          <SelectCachedOptions.Display
            value={value}
            campareBy="id"
            optionKey="ports"
            render={(option) => getLocalizedText(option?.name_ar, option?.name_en) || option?.name}
          />
        );
      },
      visible: false,
    },
    {
      name: t("ACCOMMODATION_STATUS"),
      key: "is_accommodated",
      render: (value) => {
        const is_accommodated = !!+value;

        return (
          <Tag color={is_accommodated ? "success" : "error"}>
            {is_accommodated ? t("ACCOMMODATED") : t("NOT_ACCOMMODATED")}
          </Tag>
        );
      },
      visible: false,
    },
    {
      name: t("MAKKAH_SERVICE_CENTER_NUMBER"),
      key: "makkah_service_center_id",
      render: (value) => (
        <SelectCachedOptions.Display
          value={value}
          campareBy="id"
          optionKey="service_centers"
          render={(option) => option?.name}
        />
      ),
    },
    {
      name: t("MADINAH_SERVICE_CENTER_NUMBER"),
      key: "madinah_service_center_id",
      render: (value) => (
        <SelectCachedOptions.Display
          value={value}
          campareBy="id"
          optionKey="service_centers"
          render={(option) => option?.name}
        />
      ),
    },
    {
      name: t("BAG_NUMBER"),
      key: "bag_number",
    },
    {
      name: t("OUT_PORT"),
      key: "exit_port_id",
      render: (value) => {
        return (
          <SelectCachedOptions.Display
            value={value}
            campareBy="id"
            optionKey="ports"
            render={(option) => getLocalizedText(option?.name_ar, option?.name_en) || option?.name}
          />
        );
      },
      visible: false,
    },
    { name: t("ACTIONS"), key: "actions", filterable: false },
  ];

  const filterFields = [
    {
      colSize: { md: 4 },
      name: "name",
      label: t("أسم الحاج"),
      type: "input",
    },
    {
      colSize: { md: 4 },
      name: "nationality_id",
      label: t("الجنسية"),
      type: "select",
      optionKey: "nationalities",
      options: (n) =>
        ({
          value: n.id,
          label: <SelectCountry.Display value={n.id} />,
        }) || [],
    },
    {
      colSize: { md: 4 },
      name: "mission_organizer_id",
      label: t("المنظم"),
      type: "select",
      placeholder: t("المنظم"),
      optionKey: "missions",
      options: (m) =>
        ({
          value: m.id,
          label: <LabelValue.Value value={[m.id, getLocalizedText(m.name_ar, m.name_en)].join(" - ")} />,
        }) || [],
    },
    {
      colSize: { md: 4 },
      name: "campaign_number", //group_number
      label: t("رقم الحملة / المجموعة"),
      type: "input",
    },
    {
      colSize: { md: 4 },
      name: "passport_number",
      label: t("رقم الجواز"),
      type: "input",
    },
    {
      colSize: { md: 4 },
      name: "visa_number",
      label: t("رقم التأشيرة"),
      type: "input",
    },
    {
      colSize: { md: 4 },
      name: "ad_embassy_id",
      label: t("مصدر التأشيرة"),
      type: "select",
      optionKey: "embassies",
      options: (a) =>
        ({
          value: a.id,
          label: <LabelValue.Value value={getLocalizedText(a.name_ar, a.name_en)} />,
        }) || [],
    },
    {
      colSize: { md: 4 },
      name: "service_center_id",
      label: t("مركز الخدمة"),
      type: "select",
      optionKey: "service_centers",
      options: (s) =>
        ({
          value: s.id,
          label: <LabelValue.Value value={s.name} />,
        }) || [],
    },
    {
      colSize: { md: 4 },
      name: "entry_port_id",
      label: t("منفذ الدخول"),
      type: "select",
      optionKey: "ports",
      options: (p) => ({
        value: p.id,
        label: <LabelValue.Value value={getLocalizedText(p?.name_ar, p?.name_en) || p?.name} />,
      }),
    },
    {
      colSize: { md: 4 },
      name: "nusuk_card",
      label: t("رقم بطاقة نسك"),
      type: "input",
    },
    {
      colSize: { md: 4 },
      name: "haj_type",
      label: t("نوع الحاج"),
      type: "select",
      options: [
        { label: t("HAJ"), value: "haj" },
        { label: t("MISSION_MEMBER"), value: "mission" },
      ],
    },
    {
      colSize: { md: 4 },
      name: "pilgrim_status",
      label: t("حالة الحاج"),
      type: "select",
      options: PILGRIM_STATUS.map((status) => ({
        label: getLocalizedText(status.label_ar, status.label_en),
        value: status.key,
      })),
    },
    {
      colSize: { md: 4 },
      name: "flight_number",
      label: t("رقم الرحلة"),
      type: "input",
    },
    {
      colSize: { md: 4 },
      name: "ad_camp_id",
      label: t("رقم المخيم"),
      type: "select",
      optionKey: "camps",
      options: (c) => ({
        value: c.id,
        label: <LabelValue.Value value={[c.id, c.camp_no, `(${c.camp_street_to_arafat})`].join(" - ")} />,
      }),
    },
    {
      colSize: { md: 4 },
      name: "bag_number",
      label: t("رقم الحقيبة"),
      type: "text",
    },
    {
      colSize: { md: 4 },
      name: "is_accommodated",
      label: t("حالة التسكين مكه"),
      type: "select",
      options: [
        { label: t("ACCOMMODATED"), value: "1" },
        { label: t("NOT_ACCOMMODATED"), value: "0" },
      ],
    },
    {
      colSize: { md: 4 },
      name: "accommodated_in_madina",
      label: t("حالة التسكين في المدينة"),
      type: "select",
      options: [
        { label: t("ACCOMMODATED"), value: "1" },
        { label: t("NOT_ACCOMMODATED"), value: "0" },
      ],
    },
  ];

  const pilgrims = pilgrimsRes?.data?.data.data ?? [];

  const handlePrint = (pilgrimId) => {
    setPrintPilgrimId(pilgrimId);
    setPrintRequestKey(`${pilgrimId}-${Date.now()}`);
  };

  const getActions = (rowData) => {
    return [
      {
        key: "view",
        label: (
          <Link to={`${rowData.id}/view`} className="d-flex align-items-center gap-2 text-decoration-none">
            <HugeiconsIcon size={16} icon={ViewIcon} className="flex-shrink-0" />
            <span>{t("VIEW")}</span>
          </Link>
        ),
      },
      {
        key: "print",
        onClick: () => handlePrint(rowData.id),
        label: (
          <button
            type="button"
            className="d-flex align-items-center gap-2 btn btn-link p-0 text-decoration-none"
          >
            <HugeiconsIcon size={16} icon={PrinterIcon} className="flex-shrink-0" />
            <span>{t("PRINT")}</span>
          </button>
        ),
      },
      rowData?.has_passport_receipt
        ? {
            key: "print-receipt",
            onClick:() => handlePrintReceipt(rowData),
            label: (
              <Can
                permissions={[
                  isMakkah
                    ? permissions.makkah_print_passport_receipt
                    : permissions.madina_print_passport_receipt
                ]}
              >
                <button
                  type="button"
                  className="d-flex align-items-center gap-2 btn p-0 text-decoration-none"
                  disabled={isPrintPassportReceiptPending}
                >
                  <HugeiconsIcon size={16} icon={PrinterIcon} className="flex-shrink-0" />
                  <span>{t("PRINT_RECEIPT_DOCUMENT")}</span>
                </button>
              </Can>
            ),
          }
        : {
            key: "receive",
            onClick: () => openReceiveModal(rowData),
            label: (
              <Can
                permissions={[
                  isMakkah
                    ? permissions.makkah_create_passport_receipt
                    : permissions.madina_create_passport_receipt
                ]}
              >
              <button
                type="button"
                className="d-flex align-items-center gap-2 btn p-0 text-decoration-none"
              >
                <HugeiconsIcon size={16} icon={PrinterIcon} className="flex-shrink-0" />
                <span>{t("RECEIVE_DOCUMENT")}</span>
              </button>
              </Can>
            ),
          },
    ];
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  const handleFilterApply = (filterData) => {
    setFilters(filterData);
  };

  const handleFilterReset = () => {
    setFilters({});
  };

  //
  //
  //
  //
  //
  //
  // Receive Modal
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: {
      receiver_name: "",
      receipt_date: dayjs().format(DATE_TIME_FORMAT_INPUT),
      entity_type: undefined,
      external_entity_name: "",
      internal_job_sector_id: undefined,
      receiver_mobile: "",
      receipt_reason: "",
    },
  });

  const dir = layout === "rtl" ? "ltr" : "rtl";
  const entityType = watch("entity_type");

  useEffect(() => {
    if (entityType !== "external") {
      setValue("external_entity_name", "");
    }
    if (entityType !== "internal") {
      setValue("internal_job_sector_id", undefined);
    }
  }, [entityType, setValue]);

  const closeReceiveModal = () => {
    reset({
      receiver_name: "",
      receipt_date: dayjs().format(DATE_TIME_FORMAT_INPUT),
      entity_type: undefined,
      external_entity_name: "",
      internal_job_sector_id: undefined,
      receiver_mobile: "",
      receipt_reason: "",
    });
    setSelectedPilgrim(null);
    setIsReceiveModalOpen(false);
  };

  const openReceiveModal = (pilgrim) => {
    setSelectedPilgrim(pilgrim);
    reset({
      receiver_name: "",
      receipt_date: dayjs().format(DATE_TIME_FORMAT_INPUT),
      entity_type: undefined,
      external_entity_name: "",
      internal_job_sector_id: undefined,
      receiver_mobile: "",
      receipt_reason: "",
    });
    setIsReceiveModalOpen(true);
  };

  const handleResetReceiveModal = () => {
    reset({
      receiver_name: "",
      receipt_date: dayjs().format(DATE_TIME_FORMAT_INPUT),
      entity_type: undefined,
      external_entity_name: "",
      internal_job_sector_id: undefined,
      receiver_mobile: "",
      receipt_reason: "",
    });
  };

  const handleReceiveSubmit = (data) => {
    if (!selectedPilgrim?.id) return;

    const payload = {
      receiver_name: String(data.receiver_name || "").trim(),
      receipt_date: data.receipt_date,
      entity_type: data.entity_type,
      external_entity_name:
        data.entity_type === "external" ? String(data.external_entity_name || "").trim() || null : null,
      internal_job_sector_id:
        data.entity_type === "internal" && data.internal_job_sector_id != null
          ? Number(data.internal_job_sector_id)
          : null,
      receiver_mobile: String(data.receiver_mobile || "").trim() || null,
      receipt_reason: String(data.receipt_reason || "").trim(),
    };

    createPassportReceipt(
      { pilgrimId: selectedPilgrim.id, payload },
      {
        onSuccess: () => {
          closeReceiveModal();
        },
        onError: (error) => {
          const handled = handleFormErrors(error, setError, t, "SOMETHING_WENT_WRONG");
          if (!handled) {
            toast.error(error?.response?.data?.message || t("SOMETHING_WENT_WRONG"));
          }
        },
      },
    );
  };

  const handlePrintReceipt = (pilgrim) => {
    if (!pilgrim?.id) return;

    printPassportReceipt({
      pilgrimId: pilgrim.id,
      payload: {},
    });
  };

  const receiveFields = [
    {
      name: "receiver_name",
      label: t("RECEIVER_NAME"),
      placeholder: t("RECEIVER_NAME"),
      rules: {
        required: t("FIELD_REQUIRED"),
        maxLength: { value: 300, message: t("NUSUK_MAX_CHARS", { max: 100 }) },
      },
      colSize: { md: 6 },
      inputProps: {
        maxLength: 100,
      },
    },
    {
      name: "receipt_date",
      label: t("NUSUK_RECEIPT_DATE"),
      colSize: { md: 6 },
      rules: {
        required: t("FIELD_REQUIRED"),
      },
      type: "date",
    },
    {
      name: "entity_type",
      label: t("ENTITY_TYPE"),
      placeholder: t("ENTITY_TYPE"),
      rules: {
        required: t("FIELD_REQUIRED"),
      },
      colSize: { md: 6 },
      type: "select",
      options: [
        {
          value: "external",
          label: t("EXTERNAL"),
        },
        {
          value: "internal",
          label: t("INTERNAL"),
        },
      ],
    },
    {
      name: "external_entity_name",
      label: t("EXTERNAL_SECTOR_NAME"),
      placeholder: t("EXTERNAL_SECTOR_NAME"),
      colSize: { md: 6 },
      hidden: entityType !== "external",
      rules: {
        validate: (value) => (entityType !== "external" || String(value || "").trim() ? true : t("FIELD_REQUIRED")),
      },
    },
    {
      name: "internal_job_sector_id",
      label: t("INTERNAL_SECTOR"),
      placeholder: t("INTERNAL_SECTOR"),
      colSize: { md: 6 },
      hidden: entityType !== "internal",
      type: "select",
      optionKey: "job_sectors",
      options: (sector) => ({
        value: sector.id,
        label: getLocalizedText(sector.name_ar, sector.name_en) || sector.name,
      }),
      rules: {
        validate: (value) =>
          entityType !== "internal" || (value != null && value !== "") ? true : t("FIELD_REQUIRED"),
      },
    },
    {
      name: "receiver_mobile",
      label: t("RECEIVER_MOBILE"),
      placeholder: t("RECEIVER_MOBILE"),
      colSize: { md: 6 },
      inputProps: {
        maxLength: 20,
        dir: "ltr",
        type: "tel",
      },
    },
    {
      name: "receipt_reason",
      label: t("RECEIPT_REASON"),
      placeholder: t("RECEIPT_REASON"),
      rules: {
        required: t("FIELD_REQUIRED"),
      },
      colSize: { md: 12 },
      inputProps: {
        maxLength: 255,
      },
    },
  ];

  const renderReceiveField = ({
    name,
    label,
    placeholder,
    rules,
    colSize = { md: 6 },
    inputProps = {},
    type = "input",
    options = [],
    optionKey,
    hidden = false,
  }) =>
    hidden ? null : (
      <Col key={name} {...colSize}>
        <Stack gap={1} className="mb-3">
          <Label className={`fw-bold ${rules?.required ? "required" : ""}`} for={name}>
            {label}
          </Label>
          <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field }) =>
              type === "select" ? (
                optionKey ? (
                  <SelectCachedOptions
                    {...field}
                    {...inputProps}
                    id={name}
                    optionKey={optionKey}
                    options={options}
                    placeholder={placeholder || label}
                    value={field.value ?? undefined}
                    onChange={(value) => field.onChange(value ?? undefined)}
                    status={errors[name] ? "error" : undefined}
                  />
                ) : (
                  <AntSelect
                    {...field}
                    {...inputProps}
                    id={name}
                    options={options}
                    placeholder={placeholder || label}
                    value={field.value ?? undefined}
                    onChange={(value) => field.onChange(value ?? undefined)}
                    status={errors[name] ? "error" : undefined}
                  />
                )
              ) : type === "date" ? (
                <DatePicker
                  {...inputProps}
                  id={name}
                  className="w-100"
                  size="large"
                  showTime={{ format: "HH:mm" }}
                  format={DATE_TIME_FORMAT_INPUT}
                  value={field.value ? dayjs(field.value, DATE_TIME_FORMAT_INPUT) : null}
                  onChange={(value) => field.onChange(value ? dayjs(value).format(DATE_TIME_FORMAT_INPUT) : undefined)}
                  status={errors[name] ? "error" : undefined}
                />
              ) : (
                <AntInput
                  {...field}
                  {...inputProps}
                  id={name}
                  placeholder={placeholder || label}
                  status={errors[name] ? "error" : undefined}
                />
              )
            }
          />
          {errors[name]?.message ? <small className="text-danger">{errors[name]?.message}</small> : null}
        </Stack>
      </Col>
    );
  //
  //
  //
  //
  //
  const meta = pilgrimsRes?.data?.data?.meta;
  return (
    <div>
      {isPrintPassportReceiptPending && (
        <div aria-hidden="true" style={DOCUMENT_LOADING_CURSOR_OVERLAY_STYLE} />
      )}
      <PageHeader title={t("PILGRIMS")} sticky={false} />
      <Container fluid>
        <TableC
          // basic
          data={pilgrims}
          columns={columns}
          loading={isLoading}
          // search
          searchValue={searchValue}
          onSearchChange={handleSearchChange}
          // pagination
          pagination={{
            current: page,
            total: meta?.total ?? 0,
            pageSize: meta?.per_page ?? 10,
            onChange: (page) => {
              setPage(page);
            },
          }}
          // filter
          filterFields={filterFields}
          filters={filters}
          onFilterApply={handleFilterApply}
          onFilterReset={handleFilterReset}
          // actions
          actions={getActions}
        />
        <PilgrimPrintSheet
          pilgrimId={printPilgrimId}
          printRequestKey={printRequestKey}
          onPrinted={() => {
            setPrintPilgrimId(null);
            setPrintRequestKey(null);
          }}
        />
      </Container>
      <Modal
        size="lg"
        isOpen={isReceiveModalOpen}
        toggle={closeReceiveModal}
        centered
        backdrop="static"
        keyboard={false}
      >
        <ModalHeader dir={dir} toggle={closeReceiveModal}>
          <span className="fw-bold H5">{t("PASSPORT_RECEIPT_DOCUMENT")}</span>
        </ModalHeader>
        <form onSubmit={handleSubmit(handleReceiveSubmit)}>
          <ModalBody>
            <Row>{receiveFields.map((field) => renderReceiveField(field))}</Row>
          </ModalBody>
          <ModalFooter dir={dir}>
            <Stack direction="horizontal" gap={2}>
              <Button
                color="secondary"
                size="xs"
                type="button"
                onClick={handleResetReceiveModal}
                disabled={isCreatePassportReceiptPending}
              >
                {t("RESET")}
              </Button>
              <Button
                color="danger"
                size="xs"
                type="button"
                onClick={closeReceiveModal}
                disabled={isCreatePassportReceiptPending}
              >
                {t("CANCEL")}
              </Button>
              <Button color="primary" size="xs" type="submit" disabled={isCreatePassportReceiptPending}>
                {t("RECEIVE")}
              </Button>
            </Stack>
          </ModalFooter>
        </form>
      </Modal>
    </div>
  );
};

export default Pilgrims;
